import React from 'react';

interface StringToJsxProps {
  domString: string;
}

export const StringToJsx: React.FC<StringToJsxProps> = ({ domString }) => {
  const jsxElements = useHtmlToJsx(domString);
  return <>{jsxElements}</>;
};

function useHtmlToJsx(htmlString: string): React.ReactNode[] {
  // Parse the HTML string into a document
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Remove any <script> tags
  const scripts = doc.querySelectorAll('script');
  scripts.forEach(script => script.remove());

  const body = doc.body;

  // Recursive function to convert nodes into React elements
  const createElements = (nodes: NodeList): React.ReactNode[] => {
    return Array.from(nodes).map((node, index) => {
      // Handle text nodes
      if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue;
      }

      // Handle element nodes
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element: HTMLElement = node;
        const children = createElements(node.childNodes);
        const props: React.HTMLProps<HTMLElement> = {};

        // Process attributes, converting style and class appropriately.
        if (element.attributes) {
          Array.from(element.attributes).forEach(attr => {
            let attrName = attr.name;
            if (attrName === 'class') {
              attrName = 'className';
            }
            if (attrName === 'style' && attr.value) {
              // Convert inline CSS string to a style object.
              const styleObj: React.CSSProperties = {};
              attr.value.split(';').forEach(rule => {
                const [prop, value] = rule.split(':');
                if (prop && value) {
                  styleObj[prop.trim()] = value.trim();
                }
              });
              props[attrName] = styleObj;
            } else {
              props[attrName] = attr.value;
            }
          });
        }

        // Handle self-closing tags
        if (element.tagName === 'IMG' || element.tagName === 'BR' || element.tagName === 'HR') {
          return React.createElement(
              element.tagName.toLowerCase(),
              { ...props, key: index }
          );
        }

        props.key = index;
        return React.createElement(
            element.tagName.toLowerCase(),
            props,
            children.length > 0 ? children : null
        );
      }

      // Return null for nodes that are neither text nor element.
      return null;
    });
  };

  return createElements(body.childNodes);
}